import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Joiner, mover, leaver: automated',
    description:
      'Every lifecycle event triggers the right access change on the right system, in the right order, every time.',
    bullets: [
      'New-hire access bundles by role and department',
      'Role transitions revoke and grant in one step',
      'Termination locks accounts in seconds, not days',
      'Contractor expiry with hard cut-off dates',
    ],
  },
  {
    title: 'Approvals that match the org',
    description:
      'Multi-stage approval chains follow the way your business actually decides: line manager, app owner, security, all in one flow.',
    bullets: [
      'Parallel and sequential approval steps',
      'Out-of-office routing and escalation',
      'Delegated approvals with audit trail',
      'Approve from email, mobile, or Slack',
    ],
  },
  {
    title: 'Self-service access requests',
    description:
      'Users request the access they need from a catalog. Owners approve. Monosign provisions, logs, and reviews, without a ticket queue.',
    bullets: [
      'Access catalog with searchable entitlements',
      'Time-bound and birthright access types',
      'Pre-built request templates per role',
      'Slack / Teams / email integrated approvals',
    ],
  },
  {
    title: 'Continuous certification',
    description:
      'Schedule access reviews by manager, app owner, or risk tier. Stale access dies on a calendar, not after an incident.',
    bullets: [
      'Quarterly and annual review campaigns',
      'Risk-based review prioritization',
      'Mass-approve with diff highlighting',
      'Auto-revoke unattested access',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Model your roles',
    description:
      'Build access bundles that map to roles, departments, and job functions. Monosign treats them as first-class objects.',
  },
  {
    title: 'Wire lifecycle events',
    description:
      'Connect your HRIS or AD as the trigger source. Joiner / mover / leaver events flow in automatically.',
  },
  {
    title: 'Review on a cadence',
    description:
      'Run certification campaigns by quarter, by risk tier, or on demand. Every decision is logged and exportable.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Directory & sources',
    description:
      'The identity layer that feeds lifecycle events.',
    href: '/sign/directory',
  },
  {
    label: 'Joiner / mover / leaver',
    description:
      'Use case: lifecycle automation as a discipline.',
    href: '/solutions/joiner-mover-leaver',
  },
  {
    label: 'Identity automation',
    description:
      'Rollback-safe triggers and 49 entitlement connectors.',
    href: '/sign/automation',
  },
]

export default function LifecyclePage() {
  return (
    <Layout>
      <SolutionMeta
        title="User lifecycle & provisioning"
        description="Joiner / mover / leaver workflows, access requests, and certification, automated from your HRIS to every downstream app."
        path="/sign/lifecycle"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · User lifecycle"
          title={
            <>
              Provision in minutes.
              <br />
              Deprovision in seconds.
            </>
          }
          body="From the HRIS event to every downstream app, Monosign automates joiner, mover, and leaver flows, approvals, and access reviews. No spreadsheets. No 3am tickets. No orphaned accounts."
          badges={[
            'JML automation',
            'Self-service requests',
            'Scheduled certifications',
          ]}
        />
        <SolutionPillars
          title={<>Lifecycle as code, not as a backlog.</>}
          body="Monosign lifecycle workflows replace the spreadsheets, ticket queues, and back-channel emails that most organizations still call provisioning."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From event to access, every time.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
